import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { createOrder } from '../../services/orderApi';
import './ContactForm.css';

/**
 * Страница формы обратной связи
 */
const ContactForm = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: 'USA',
    state: '',
    city: '',
    zipCode: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    ssn: '',
    itin: '',
    ein: '',
    agreeTerms: false,
    agreePrivacy: false
  });

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Секции для навигации в Header
  const sections = [
    { id: 'hero', label: 'Home' },
    { id: 'testimonial', label: 'About Us' },
    { id: 'legal-help', label: 'Services' },
    { id: 'success-stories', label: 'Success Stories' },
    { id: 'features', label: 'Features' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);

    try {
      // Подготовка данных для API
      const orderData = {
        email: formData.email,
        phone: formData.phone,
        country: formData.country === 'USA' ? 'US' : formData.country,
        state: formData.state,
      };

      console.log('Creating order:', orderData);

      // Создание заказа через API
      const createdOrder = await createOrder(orderData);

      console.log('Order created successfully:', createdOrder);

      // Сохраняем ID заказа в localStorage для дальнейшего использования
      localStorage.setItem('currentOrderId', createdOrder.id);
      localStorage.setItem('orderData', JSON.stringify({
        ...createdOrder,
        firstName: formData.firstName,
        lastName: formData.lastName,
        city: formData.city,
        zipCode: formData.zipCode,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        addressLine3: formData.addressLine3,
        ssn: formData.ssn,
        itin: formData.itin,
        ein: formData.ein,
      }));

      // Переадресация на страницу тарифов
      navigate('/pricing-plans');
    } catch (error) {
      console.error('Error creating order:', error);
      setSubmitError('Failed to submit the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-form-page">
      <Header sections={sections} onJoinClick={openModal} />
      
      {/* Фон с двумя зелеными кругами */}
      <div className="page-background">
        <div className="green-circle green-circle-1"></div>
        <div className="green-circle green-circle-2"></div>
      </div>

      {/* Центральное окно с формой */}
      <main className="content-container">
        <div className="content-box">
          <div className="form-header">
            <p className="form-badge">You're almost there! 🎉</p>
            <h1 className="page-title">Fill out the form</h1>
            <p className="page-subtitle">
              Help us understand your situation better so we can explore your debt history and be able to help you
            </p>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            {/* Contact Information */}
            <section className="form-section">
              <h2 className="section-title">Contact Information</h2>
              <p className="section-subtitle">Your name — or contact information</p>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="John"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="example@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                  required
                />
              </div>
            </section>

            {/* Address */}
            <section className="form-section">
              <h2 className="section-title">Address</h2>
              <p className="section-subtitle">Your address — or your address</p>
              
              <div className="form-group">
                <label htmlFor="country">Country *</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                >
                  <option value="USA">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="UK">United Kingdom</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="addressLine1">Address Line 1 *</label>
                <input
                  type="text"
                  id="addressLine1"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleInputChange}
                  placeholder="123 Main Street"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="addressLine2">Address Line 2</label>
                <input
                  type="text"
                  id="addressLine2"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleInputChange}
                  placeholder="Apartment, suite, etc. (optional)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="addressLine3">Address Line 3</label>
                <input
                  type="text"
                  id="addressLine3"
                  name="addressLine3"
                  value={formData.addressLine3}
                  onChange={handleInputChange}
                  placeholder="Additional address info (optional)"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="New York"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state">State *</label>
                  <select
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select State</option>
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option>
                    <option value="CA">California</option>
                    <option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option>
                    <option value="DE">Delaware</option>
                    <option value="FL">Florida</option>
                    <option value="GA">Georgia</option>
                    <option value="HI">Hawaii</option>
                    <option value="ID">Idaho</option>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="IA">Iowa</option>
                    <option value="KS">Kansas</option>
                    <option value="KY">Kentucky</option>
                    <option value="LA">Louisiana</option>
                    <option value="ME">Maine</option>
                    <option value="MD">Maryland</option>
                    <option value="MA">Massachusetts</option>
                    <option value="MI">Michigan</option>
                    <option value="MN">Minnesota</option>
                    <option value="MS">Mississippi</option>
                    <option value="MO">Missouri</option>
                    <option value="MT">Montana</option>
                    <option value="NE">Nebraska</option>
                    <option value="NV">Nevada</option>
                    <option value="NH">New Hampshire</option>
                    <option value="NJ">New Jersey</option>
                    <option value="NM">New Mexico</option>
                    <option value="NY">New York</option>
                    <option value="NC">North Carolina</option>
                    <option value="ND">North Dakota</option>
                    <option value="OH">Ohio</option>
                    <option value="OK">Oklahoma</option>
                    <option value="OR">Oregon</option>
                    <option value="PA">Pennsylvania</option>
                    <option value="RI">Rhode Island</option>
                    <option value="SC">South Carolina</option>
                    <option value="SD">South Dakota</option>
                    <option value="TN">Tennessee</option>
                    <option value="TX">Texas</option>
                    <option value="UT">Utah</option>
                    <option value="VT">Vermont</option>
                    <option value="VA">Virginia</option>
                    <option value="WA">Washington</option>
                    <option value="WV">West Virginia</option>
                    <option value="WI">Wisconsin</option>
                    <option value="WY">Wyoming</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="zipCode">Zip Code *</label>
                <input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  placeholder="10001"
                  required
                />
              </div>
            </section>

            {/* Identification */}
            <section className="form-section">
              <h2 className="section-title">Identification</h2>
              <p className="section-subtitle">
                At least one - If you do not want to specify this, click on the button below
              </p>
              
              <div className="form-group">
                <label htmlFor="ssn">SSN (Social Security Number)</label>
                <input
                  type="text"
                  id="ssn"
                  name="ssn"
                  value={formData.ssn}
                  onChange={handleInputChange}
                  placeholder="XXX-XX-XXXX"
                  maxLength="11"
                />
              </div>

              <div className="form-group">
                <label htmlFor="itin">ITIN (Individual Taxpayer Identification Number)</label>
                <input
                  type="text"
                  id="itin"
                  name="itin"
                  value={formData.itin}
                  onChange={handleInputChange}
                  placeholder="XXX-XX-XXXX"
                  maxLength="11"
                />
              </div>

              <div className="form-group">
                <label htmlFor="ein">EIN (Employer Identification Number)</label>
                <input
                  type="text"
                  id="ein"
                  name="ein"
                  value={formData.ein}
                  onChange={handleInputChange}
                  placeholder="XX-XXXXXXX"
                  maxLength="10"
                />
              </div>

              <p className="form-note">
                Make sure that you entered all the information correctly
              </p>
            </section>

            {/* Agreement Checkboxes */}
            <section className="form-section">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
                <label htmlFor="agreeTerms">
                  I agree with the <a href="/terms-of-service" className="form-link">terms and conditions</a> *
                </label>
              </div>

              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="agreePrivacy"
                  name="agreePrivacy"
                  checked={formData.agreePrivacy}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
                <label htmlFor="agreePrivacy">
                  I agree with the <a href="/privacy-policy" className="form-link">privacy policy</a> *
                </label>
              </div>
            </section>

            {/* Error Message */}
            {submitError && (
              <div className="contact-form-page__error-message">
                <p>{submitError}</p>
              </div>
            )}

            {/* Submit Button */}
            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Send the Form'}
            </button>
          </form>
        </div>
      </main>

      <Footer />

      {/* Modal Window */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Are you sure you want to continue?</h3>
            <div className="modal-buttons">
              <button className="modal-confirm-button" onClick={closeModal}>
                Yes
              </button>
              <button className="modal-cancel-button" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactForm;
